<template>
  <div class="page-container">
    <div class="tab-content">
      <h4>API Tester</h4>
      <p class="page-subtitle">Dev and testing tool for inspecting raw API responses.</p>

      <div class="section-card">
        <div class="section-body">
          <div class="form-grid">
            <div class="form-field">
              <label>Base Prefix</label>
              <select v-model="selectedPreset" class="custom-input full-width" @change="onPresetChange">
                <option value="data_prod">/api/v1.1/data (Data API — Prod)</option>
                <option value="data_dev">/dev-api/api/v1.1/data (Data API — Dev)</option>
                <option value="auth_prod">/api (Auth — Prod)</option>
                <option value="auth_dev">/dev-api/api (Auth — Dev)</option>
                <option value="custom">Custom</option>
              </select>
            </div>

            <div v-if="selectedPreset === 'custom'" class="form-field">
              <label>Custom Base Path</label>
              <input
                v-model="customBasePath"
                class="custom-input full-width"
                type="text"
                placeholder="/api/v1.1/data"
              />
            </div>

            <div class="form-field">
              <label>Endpoint Suffix</label>
              <input
                v-model="endpointSuffix"
                class="custom-input full-width"
                type="text"
                placeholder="ai_pool_users/get"
              />
            </div>

            <div class="form-field">
              <label>HTTP Method</label>
              <div class="options">
                <button
                  class="button-option"
                  :class="{ active: method === 'POST' }"
                  @click="method = 'POST'"
                >
                  POST
                </button>
                <button
                  class="button-option"
                  :class="{ active: method === 'GET' }"
                  @click="method = 'GET'"
                >
                  GET
                </button>
              </div>
            </div>

            <div class="form-field">
              <label>Request Body (JSON)</label>
              <textarea
                v-model="requestBody"
                class="custom-input full-width code-area"
                :disabled="method === 'GET'"
                rows="6"
                placeholder='{ "key": "value" }'
              />
            </div>

            <div class="form-field">
              <label>Query Params (JSON, optional)</label>
              <textarea
                v-model="queryParams"
                class="custom-input full-width code-area"
                rows="3"
                placeholder='{ "page": 1 }'
              />
            </div>

            <div class="resolved-url">
              <label>Resolved URL</label>
              <code>{{ resolvedUrl || '—' }}</code>
            </div>

            <div v-if="validationError" class="validation-error">
              {{ validationError }}
            </div>

            <div class="action-row">
              <button
                class="custom-button active"
                :disabled="isLoading || !endpointSuffix.trim()"
                @click="sendRequest"
              >
                {{ isLoading ? 'Sending...' : 'Send Request' }}
              </button>
              <button class="custom-button" :disabled="!result" @click="clearResults">
                Clear Results
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="result" class="section-card">
        <div class="section-header-static">Response</div>
        <div class="section-body">
          <div class="summary-grid">
            <div class="summary-item">
              <span class="summary-label">Status</span>
              <span :class="['status-badge', result.isError ? 'error' : 'success']">
                {{ result.status }} {{ result.statusText }}
              </span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Duration</span>
              <span>{{ result.durationMs }} ms</span>
            </div>
            <div class="summary-item">
              <span class="summary-label">Method</span>
              <span>{{ result.method }}</span>
            </div>
            <div class="summary-item full-span">
              <span class="summary-label">URL</span>
              <code class="url-text">{{ result.url }}</code>
            </div>
          </div>

          <div v-if="result.errorMessage" class="error-block">
            <strong>Error:</strong> {{ result.errorMessage }}
          </div>

          <div class="response-section">
            <div class="section-toolbar">
              <h6>Response Body</h6>
              <button class="custom-button small" @click="copyToClipboard(result.bodyText)">
                Copy
              </button>
            </div>
            <pre class="code-block">{{ result.bodyText }}</pre>
          </div>

          <div class="response-section">
            <h6>Response Headers</h6>
            <pre class="code-block">{{ result.headersText }}</pre>
          </div>

          <div class="response-section">
            <h6>Request Details</h6>
            <pre class="code-block">{{ result.requestDetailsText }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/apiService';
import { getDevModeState } from '@/services/cookieService';
import { eventBus } from '@/services/eventBus';

const PRESET_PATHS = {
  data_prod: '/api/v1.1/data',
  data_dev: '/dev-api/api/v1.1/data',
  auth_prod: '/api',
  auth_dev: '/dev-api/api',
};

export default {
  name: 'ApiTesterPage',
  data() {
    return {
      selectedPreset: 'data_prod',
      customBasePath: '',
      endpointSuffix: '',
      method: 'POST',
      requestBody: '{}',
      queryParams: '',
      isLoading: false,
      validationError: '',
      result: null,
      isDevMode: false,
    };
  },
  computed: {
    basePrefix() {
      if (this.selectedPreset === 'custom') {
        return this.customBasePath.trim();
      }
      return PRESET_PATHS[this.selectedPreset] || '';
    },
    resolvedUrl() {
      if (!this.endpointSuffix.trim()) {
        return '';
      }
      const base = this.basePrefix.replace(/\/+$/, '');
      const suffix = this.endpointSuffix.trim().replace(/^\/+/, '');
      return `${base}/${suffix}`;
    },
  },
  methods: {
    onPresetChange() {
      if (this.selectedPreset === 'custom' && !this.customBasePath) {
        this.customBasePath = PRESET_PATHS.data_prod;
      }
    },
    handleEnvironmentChange(isDevMode) {
      this.isDevMode = isDevMode;
      if (this.selectedPreset === 'data_prod' || this.selectedPreset === 'data_dev') {
        this.selectedPreset = isDevMode ? 'data_dev' : 'data_prod';
      } else if (this.selectedPreset === 'auth_prod' || this.selectedPreset === 'auth_dev') {
        this.selectedPreset = isDevMode ? 'auth_dev' : 'auth_prod';
      }
    },
    initializeEnvironment() {
      const devModeState = getDevModeState();
      if (devModeState.isDevMode !== undefined) {
        this.isDevMode = devModeState.isDevMode;
        this.selectedPreset = devModeState.isDevMode ? 'data_dev' : 'data_prod';
      }
    },
    parseJsonInput(value, fieldName) {
      if (!value || !value.trim()) {
        return null;
      }
      try {
        return JSON.parse(value);
      } catch {
        throw new Error(`Invalid JSON in ${fieldName}`);
      }
    },
    maskHeaders(headers) {
      if (!headers) {
        return {};
      }
      const masked = { ...headers };
      if (masked.token) {
        masked.token = '***';
      }
      return masked;
    },
    formatJson(value) {
      if (value === undefined || value === null) {
        return '';
      }
      if (typeof value === 'string') {
        return value;
      }
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return String(value);
      }
    },
    buildResult({ response, error, durationMs, parsedBody, parsedParams }) {
      const axiosResponse = response || (error && error.response);
      const status = axiosResponse ? axiosResponse.status : 0;
      const statusText = axiosResponse ? axiosResponse.statusText : 'Network Error';
      const headers = axiosResponse ? axiosResponse.headers : {};
      const data = axiosResponse ? axiosResponse.data : null;
      const config = axiosResponse ? axiosResponse.config : (error && error.config);

      const requestDetails = {
        method: this.method,
        url: this.resolvedUrl,
        body: this.method === 'POST' ? parsedBody : null,
        params: parsedParams,
        headers: config ? this.maskHeaders(config.headers) : {},
      };

      return {
        isError: !!error,
        status,
        statusText,
        durationMs,
        method: this.method,
        url: config?.url || this.resolvedUrl,
        errorMessage: error ? (error.message || 'Request failed') : '',
        bodyText: this.formatJson(data),
        headersText: this.formatJson(headers),
        requestDetailsText: this.formatJson(requestDetails),
      };
    },
    async sendRequest() {
      this.validationError = '';
      this.result = null;

      if (!this.endpointSuffix.trim()) {
        this.validationError = 'Endpoint suffix is required.';
        return;
      }

      if (!this.basePrefix) {
        this.validationError = 'Base prefix is required.';
        return;
      }

      let parsedBody = null;
      let parsedParams = null;

      try {
        if (this.method === 'POST') {
          parsedBody = this.parseJsonInput(this.requestBody, 'request body');
        }
        parsedParams = this.parseJsonInput(this.queryParams, 'query params');
      } catch (err) {
        this.validationError = err.message;
        return;
      }

      this.isLoading = true;
      const start = performance.now();

      try {
        const response = await apiClient({
          method: this.method.toLowerCase(),
          url: this.resolvedUrl,
          data: this.method === 'POST' ? parsedBody : undefined,
          params: parsedParams || undefined,
        });

        const durationMs = Math.round(performance.now() - start);
        this.result = this.buildResult({
          response,
          error: null,
          durationMs,
          parsedBody,
          parsedParams,
        });
      } catch (error) {
        const durationMs = Math.round(performance.now() - start);
        this.result = this.buildResult({
          response: null,
          error,
          durationMs,
          parsedBody,
          parsedParams,
        });
      } finally {
        this.isLoading = false;
      }
    },
    clearResults() {
      this.result = null;
      this.validationError = '';
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // Clipboard access may be unavailable
      }
    },
  },
  created() {
    this.initializeEnvironment();
    eventBus.on('environment-changed', this.handleEnvironmentChange);
  },
  beforeUnmount() {
    eventBus.off('environment-changed', this.handleEnvironmentChange);
  },
};
</script>

<style scoped>
.page-container {
  margin: 0;
  padding: 0;
}

.tab-content {
  margin: 20px;
  padding: 20px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: var(--bg-primary);
}

.page-subtitle {
  color: var(--text-tertiary);
}

.section-card {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  overflow: hidden;
  margin-top: 20px;
}

.section-header-static {
  padding: 14px 16px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  font-weight: 600;
}

.section-body {
  padding: 16px;
  background: var(--card-bg);
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-field label {
  display: block;
  margin-bottom: 6px;
  color: var(--text-secondary);
  font-size: 0.85rem;
}

.options {
  display: flex;
  gap: 10px;
}

.button-option {
  cursor: pointer;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 0.8rem;
  padding: 10px 16px;
  transition: all 0.2s ease;
}

.button-option.active {
  border: 2px solid #20cc20;
  background: #20cc20;
  color: #ffffff;
  font-weight: 600;
}

.custom-input {
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--input-bg);
  color: var(--text-primary);
  padding: 10px 12px;
}

.full-width {
  width: 100%;
  box-sizing: border-box;
}

.code-area {
  font-family: monospace;
  resize: vertical;
}

.custom-button {
  cursor: pointer;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
  border-radius: 5px;
  font-size: 14px;
  padding: 10px 14px;
}

.custom-button.active {
  border-color: #20cc20;
}

.custom-button.small {
  font-size: 12px;
  padding: 6px 10px;
}

.custom-button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.resolved-url code {
  display: block;
  margin-top: 4px;
  padding: 8px 10px;
  background: var(--bg-secondary);
  border-radius: 6px;
  font-size: 0.85rem;
  word-break: break-all;
}

.validation-error {
  color: #e74c3c;
  font-size: 0.9rem;
}

.action-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-item.full-span {
  grid-column: 1 / -1;
}

.summary-label {
  font-size: 0.8rem;
  color: var(--text-tertiary);
}

.status-badge {
  font-weight: 600;
}

.status-badge.success {
  color: #20cc20;
}

.status-badge.error {
  color: #e74c3c;
}

.url-text {
  word-break: break-all;
  font-size: 0.85rem;
}

.error-block {
  padding: 10px 12px;
  margin-bottom: 14px;
  border-radius: 6px;
  background: rgba(231, 76, 60, 0.1);
  color: #e74c3c;
}

.response-section {
  margin-top: 16px;
}

.response-section h6 {
  margin: 0 0 8px;
  color: var(--text-primary);
}

.section-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.section-toolbar h6 {
  margin: 0;
}

.code-block {
  margin: 0;
  padding: 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  overflow: auto;
  max-height: 400px;
  font-size: 0.8rem;
  color: var(--text-primary);
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 750px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }

  .options,
  .action-row {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
